/**
 * Delivery method label helper — resolves partner's country to a localized
 * label like "한국 내 배송" / "Delivery within Korea" / "日本国内配送".
 *
 * Architecture:
 *   - i18n owns 1 template per locale, e.g. ko: "{country} 내 배송"
 *   - This file owns the short country-name map for 22+ countries
 *   - Adding a new partner country = add 1 line here, zero i18n changes
 */

import type { Locale } from "@/i18n/routing";

type SupportedLocale = Locale;

/**
 * Short, label-friendly country names for delivery labels.
 * Differs from COUNTRIES config where ko name is the formal "대한민국".
 * Label context wants the short "한국" form for natural reading.
 */
const DELIVERY_COUNTRY_NAMES: Record<string, Record<SupportedLocale, string>> = {
  KR: { ko: "한국", en: "Korea", ja: "韓国", "zh-CN": "韩国", "zh-TW": "韓國" },
  JP: { ko: "일본", en: "Japan", ja: "日本", "zh-CN": "日本", "zh-TW": "日本" },
  CN: { ko: "중국", en: "China", ja: "中国", "zh-CN": "中国", "zh-TW": "中國" },
  TW: { ko: "대만", en: "Taiwan", ja: "台湾", "zh-CN": "台湾", "zh-TW": "台灣" },
  HK: { ko: "홍콩", en: "Hong Kong", ja: "香港", "zh-CN": "香港", "zh-TW": "香港" },
  SG: { ko: "싱가포르", en: "Singapore", ja: "シンガポール", "zh-CN": "新加坡", "zh-TW": "新加坡" },
  TH: { ko: "태국", en: "Thailand", ja: "タイ", "zh-CN": "泰国", "zh-TW": "泰國" },
  VN: { ko: "베트남", en: "Vietnam", ja: "ベトナム", "zh-CN": "越南", "zh-TW": "越南" },
  MY: { ko: "말레이시아", en: "Malaysia", ja: "マレーシア", "zh-CN": "马来西亚", "zh-TW": "馬來西亞" },
  ID: { ko: "인도네시아", en: "Indonesia", ja: "インドネシア", "zh-CN": "印度尼西亚", "zh-TW": "印尼" },
  PH: { ko: "필리핀", en: "Philippines", ja: "フィリピン", "zh-CN": "菲律宾", "zh-TW": "菲律賓" },
  IN: { ko: "인도", en: "India", ja: "インド", "zh-CN": "印度", "zh-TW": "印度" },
  US: { ko: "미국", en: "United States", ja: "アメリカ", "zh-CN": "美国", "zh-TW": "美國" },
  CA: { ko: "캐나다", en: "Canada", ja: "カナダ", "zh-CN": "加拿大", "zh-TW": "加拿大" },
  MX: { ko: "멕시코", en: "Mexico", ja: "メキシコ", "zh-CN": "墨西哥", "zh-TW": "墨西哥" },
  BR: { ko: "브라질", en: "Brazil", ja: "ブラジル", "zh-CN": "巴西", "zh-TW": "巴西" },
  AR: { ko: "아르헨티나", en: "Argentina", ja: "アルゼンチン", "zh-CN": "阿根廷", "zh-TW": "阿根廷" },
  CL: { ko: "칠레", en: "Chile", ja: "チリ", "zh-CN": "智利", "zh-TW": "智利" },
  GB: { ko: "영국", en: "United Kingdom", ja: "イギリス", "zh-CN": "英国", "zh-TW": "英國" },
  FR: { ko: "프랑스", en: "France", ja: "フランス", "zh-CN": "法国", "zh-TW": "法國" },
  DE: { ko: "독일", en: "Germany", ja: "ドイツ", "zh-CN": "德国", "zh-TW": "德國" },
  AU: { ko: "호주", en: "Australia", ja: "オーストラリア", "zh-CN": "澳大利亚", "zh-TW": "澳洲" },
};

function isSupportedLocale(locale: string): locale is SupportedLocale {
  return locale === "ko" || locale === "en" || locale === "ja" || locale === "zh-CN" || locale === "zh-TW";
}

/**
 * Localized country name for `countryMismatch` validation message
 * (e.g., "이 배송 옵션은 한국 내 주소로만 이용 가능합니다.").
 * Returns null when country is unknown so caller can fall back.
 */
export function getDeliveryCountryName(
  countryCode: string | null | undefined,
  locale: string,
): string | null {
  if (!countryCode) return null;
  const names = DELIVERY_COUNTRY_NAMES[countryCode.toUpperCase()];
  if (!names) return null;
  const localeKey: SupportedLocale = isSupportedLocale(locale) ? locale : "ko";
  return names[localeKey];
}

/**
 * Resolve the localized label for the `delivery` usage method based on the
 * partner's country.
 *
 * - `templateKey`: i18n key holding the country-interpolated template, e.g.
 *   "deliveryTemplate" pointing to "{country} 내 배송" (ko) or
 *   "Delivery within {country}" (en).
 * - `fallbackKey`: i18n key shown when partner's country is unknown.
 *
 * Example (PDP):
 *   const t = useTranslations("product.usageMethods");
 *   const label = getDeliveryLabel(partner.country_code, locale, t, {
 *     templateKey: "deliveryTemplate",
 *     fallbackKey: "delivery",
 *   });
 */
export function getDeliveryLabel(
  countryCode: string | null | undefined,
  locale: string,
  t: (key: string, params?: Record<string, string>) => string,
  options: { templateKey: string; fallbackKey: string },
): string {
  const name = getDeliveryCountryName(countryCode, locale);
  if (!name) return t(options.fallbackKey);
  return t(options.templateKey, { country: name });
}
